import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	useController,
	useForm,
} from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
	Form,
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import {
	useGetFieldOptionCommonDetailQuery,
	useGetFieldOptionCommonListQuery,
	useGetFieldOptionRegionDetailQuery,
	useGetFieldOptionRegionListQuery,
	useUpdateFieldOptionCommonDetailStatusMutation,
	useUpdateFieldOptionRegionDetailStatusMutation,
} from '@/api/field-option/fieldOption';
import dayjs from 'dayjs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { FIELD_UNIT_STATUS } from '@/common/constants/common';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
	title?: string;
	type?: number;
}
export default function FieldCommonStatusPage(props: Props) {
	const { title, type } = props;
	const { fieldId, fieldType, agencyType, referralType, agencyId, referralId } =
		useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [updateFieldOptionCommonDetailStatus] =
		useUpdateFieldOptionCommonDetailStatusMutation();

	const { data: dictDetails } = useGetFieldOptionCommonDetailQuery(
		fieldType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 1 && !fieldType,
		}
	);
	const { data: agencyDetails } = useGetFieldOptionCommonDetailQuery(
		agencyType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 2 && !agencyType,
		}
	);
	const { data: referralDetails } = useGetFieldOptionCommonDetailQuery(
		referralType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 3 && !referralType,
		}
	);

	const defaultValues: DefaultValues<any> = {};
	const methods = useForm<any>({
		defaultValues,
	});
	console.log(type);

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFieldOptionCommonListQuery(
			{
				pagination: { page: 1, size: 99999 },
				searchString: '',
				sortings: [],
				type: type === 2 ? agencyType : type === 3 ? referralType : fieldType,
				parentId: type === 2 ? agencyId : type === 3 ? referralId : '',
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	const fieldList = useController({
		name: 'fieldList',
		control: methods.control,
	});

	useEffect(() => {
		if (propertySummary) {
			if (propertySummary?.data?.items) {
				methods.setValue(
					'fieldList',
					(propertySummary?.data?.items || []).map((item: any) => {
						return { ...item, status: item.status ? true : false };
					})
				);
			}
		}
	}, [propertySummary]);

	const columns: GridColDef[] = [
		...(type === 1 && dictDetails?.data.hasCode
			? [
					{
						field: 'code',
						headerName: t('field.code'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 2 && agencyDetails?.data.hasCode
			? [
					{
						field: 'code',
						headerName: t('field.code'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 3 && referralDetails?.data.hasCode
			? [
					{
						field: 'code',
						headerName: t('field.code'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 4 && dictDetails?.data.hasCode
			? [
					{
						field: 'code',
						headerName: t('field.code'),
						flex: 20,
					},
			  ]
			: []),
		{
			field: 'labelEn',
			headerName: t('field.en'),
			flex: 20,
		},
		...(type === 1 && dictDetails?.data.hasChinese
			? [
					{
						field: 'labelZh',
						headerName: t('field.zh'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 2 && agencyDetails?.data.hasChinese
			? [
					{
						field: 'labelZh',
						headerName: t('field.zh'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 3 && referralDetails?.data.hasChinese
			? [
					{
						field: 'labelZh',
						headerName: t('field.zh'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 4 && dictDetails?.data.hasChinese
			? [
					{
						field: 'labelZh',
						headerName: t('field.zh'),
						flex: 20,
					},
			  ]
			: []),

		...(fieldType === 'nature'
			? [
					{
						field: 'needToEnterRiskLevel',
						headerName: t('field.needEnterRiskLevel'),
						flex: 20,
						renderCell: (params: any) =>
							params.row.needToEnterRiskLevel ? 'Yes' : 'No',
					},
			  ]
			: []),
		...(fieldType === 'nature'
			? [
					{
						field: 'countAsAMeetingIntervention',
						headerName: t('field.countMeetingIntervention'),
						flex: 20,
						renderCell: (params: any) =>
							params.row.countAsAMeetingIntervention ? 'Yes' : 'No',
					},
			  ]
			: []),
		...(fieldType === 'nature'
			? [
					{
						field: 'countAsAProgramMemberRenewalIntervention',
						headerName: t('field.countProgramMemberRenewalIntervention'),
						flex: 20,
						renderCell: (params: any) =>
							params.row.countAsAProgramMemberRenewalIntervention
								? 'Yes'
								: 'No',
					},
			  ]
			: []),

		{
			field: 'updatedAt',
			headerName: t('field.lastUpdated'),
			flex: 20,
		},
		{
			field: 'status',
			headerName: t('field.status'),
			flex: 40,
			renderCell: (params: any) => {
				const allRows = methods.watch('fieldList') || [];
				const correctIndex = allRows.findIndex(
					(x: any) => x.id === params.row.id
				);

				return (
					<AppRadioGroup
						control={methods.control}
						name={`fieldList[${correctIndex}].status`}
						row
						options={FIELD_UNIT_STATUS}
					/>
				);
			},
		},
	];

	const handleSubmit = async () => {
		const response = await updateFieldOptionCommonDetailStatus({
			updateStatusDTOList: methods.getValues('fieldList'),
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFieledUnitStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				type === 2
					? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}`
					: type === 3
					? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}/referral/${referralId}/${referralType}`
					: `/field-management/field-management-list/field/${fieldId}/${fieldType}`,
				{ state: { refresh: true } }
			);
		}
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{type === 2
						? `${t('field.manageStatus')} (${agencyDetails?.data.labelDisplay})`
						: type === 3
						? `${t('field.manageStatus')} (${
								referralDetails?.data.labelDisplay
						  })`
						: `${t('field.manageStatus')} (${dictDetails?.data.labelDisplay})`}
				</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<Box marginTop={2}>
							<LocalDataGrid
								rows={transformResponse(methods.getValues('fieldList')) ?? []}
								columns={columns}
								getRowId={(row) => row.id}
								pagination={false}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
				<Button
					onClick={() => {
						navigate(
							type === 2
								? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}`
								: type === 3
								? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}/referral/${referralId}/${referralType}`
								: `/field-management/field-management-list/field/${fieldId}/${fieldType}`
						);
					}}
				>
					{t('button.cancel')}
				</Button>
				<Button type='submit' variant='contained' onClick={handleSubmit}>
					{t('button.complete')}
				</Button>
			</Box>
		</>
	);
}
